import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { isToday } from 'date-fns';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { THREE, ZERO } from '@/constants';
import { useCurrentUser } from '@/hooks';
import {
  isAdmin,
  isCoach,
  isMomentsSession,
  isSupport,
  SessionListType,
  SessionStatus,
} from '@/types';
import { Avatar, Button, Icon, Paper } from '@/ui';
import { track } from '@/utils';

import Feedback from '../Feedback';
import SessionReflectionChip from '../ReflectionChip';
import {
  isMemberReflectionProfile,
  MEMBER_REFLECTION_PROFILE_SLUGS,
} from '../memberReflectionProfiles';
import { useSessionNotTranscribed } from '../useSessionNotTranscribed';

import SessionCardInfo from './Info';
import SessionCardMenu from './Menu';
import SummaryLink from './SummaryLink';

import type { Session } from '@/types';
import type { FC } from 'react';

import { useSessionJoinUrl } from '@/features/appointments/useSessionJoinUrl';
import { useGetMomentsSessionArtifactsQuery } from '@/store/api/momentsApi';
import { useGetSharedArtifactBySessionQuery } from '@/store/api/sharedArtifactApi';
import { useAppDispatch } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';

const SessionCard: FC<{
  enforceMemberRescheduleMinimumNotice?: boolean;
  isEmbedded?: boolean; // if true, its on overview
  onCancel?: (session: Session) => void;
  onClickPath?: string;
  onFeedback?: (session: Session) => void;
  onReschedule?: (session: Session) => void;
  presentation?: 'default' | 'matched';
  session: Session;
  type: SessionListType;
}> = ({
  enforceMemberRescheduleMinimumNotice = undefined,
  isEmbedded = false,
  onCancel = undefined,
  onFeedback = undefined,
  onReschedule = undefined,
  presentation = 'default',
  session,
  type,
  onClickPath = '',
}) => {
  const dispatch = useAppDispatch();

  const {
    currentData: currentUserData,
    isError: isCurrentUserError,
    isUserLoading,
    user,
  } = useCurrentUser();
  const navigate = useNavigate();
  // Rows produced by `appointmentActionSession` carry Moments person ids
  // in `session.coach.id` / `session.user.id` (not Rails `User.id`), an
  // appointment UUID in `session.id`, and an empty `cometChatId` from
  // the adapter. Suppress every legacy navigation and profile/messaging
  // action for those rows so a click cannot land on a 404 detail route,
  // a wrong-person profile page, or an empty chat. Reschedule and
  // Cancel still work through the native dialogs, which read the
  // appointment-specific fields on `Session`.
  //
  // The gate must NOT fire on a legacy backend Session projection that
  // simply references a native appointment via `sourceAppointmentId` —
  // those rows carry real Rails ids and their navigation / message /
  // profile CTAs are valid. The adapter's structural signature is that
  // `session.id` equals `session.sourceAppointmentId` (both are set to
  // the appointment UUID). A backend projection uses a Rails Session
  // id for `session.id` which cannot equal an appointment UUID.
  const isNativeAppointmentRow =
    !!session.sourceAppointmentId && session.id === session.sourceAppointmentId;
  // Only `appointmentActionSession` rows carry Moments person ids in
  // `session.coachId`, which can never equal the Rails `user.id` the viewer is
  // identified by; comparing them resolves every such row to "viewer is the
  // member" and shows a Moments-native coach their own name and avatar as the
  // counterparty (MEN-4915). Those rows are decided by viewer role instead,
  // which is unambiguous because the participant source is only enabled for a
  // member or a single-role Moments coach. `adminAppointmentActionSession`
  // rows are also native but carry Rails user ids, so they keep the precise id
  // compare -- as do legacy rows. The scheme is read from the adapter rather
  // than inferred, because both adapters set `id === sourceAppointmentId`.
  const isUserSessionCoach =
    session.sourceAppointmentIdentityScheme === 'moments-person'
      ? isCoach(user)
      : user.id === session.coachId;
  const concerningUser = isUserSessionCoach ? session.user : session.coach;
  const isMemberRescheduleActor =
    !isCoach(user) &&
    !isAdmin(user) &&
    !isSupport(user) &&
    (user.id === session.userId || isNativeAppointmentRow);
  const shouldQuerySharedSummary =
    !isNativeAppointmentRow &&
    !isEmbedded &&
    type === SessionListType.Past &&
    !!session.id;
  const shouldQueryReflection =
    shouldQuerySharedSummary &&
    !isUserLoading &&
    !isCurrentUserError &&
    !!currentUserData?.id &&
    currentUserData.id === user.id &&
    user.id === session.userId;
  const { data: sharedSummary } = useGetSharedArtifactBySessionQuery(
    { session_id: session.id },
    { skip: !shouldQuerySharedSummary },
  );
  const { data: momentsSessionArtifacts } = useGetMomentsSessionArtifactsQuery(
    shouldQueryReflection
      ? {
          includeContent: true,
          legacySessionId: session.id,
          profileSlug: MEMBER_REFLECTION_PROFILE_SLUGS,
        }
      : skipToken,
  );

  const handleOpenMenuOptions = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    track('Clicked Session Card Menu');
  };

  const handleViewProfile = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!isEmbedded) {
      track('Clicked Profile Session Card', {
        userId: session.userId,
        coachUserId: session.coachId,
        isCurrentUserCoach: isUserSessionCoach,
        sessionId: session.id,
      });
      if (!isUserSessionCoach) {
        navigate(`/m/profiles-coach/${concerningUser.id}`);
      } else {
        navigate(`/coach/members/${concerningUser.id}`);
      }
    }
  };

  const handleOnMessage = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    track('Clicked Message Participant', {
      userId: session.userId,
      coachUserId: session.coachId,
      isCurrentUserCoach: isUserSessionCoach,
    });
    if (!isUserSessionCoach) {
      dispatch(setChatId(session.coach.cometChatId));
    } else {
      dispatch(setChatId(session.user.cometChatId));
    }
  };
  const onSessionClick = () => {
    if (isNativeAppointmentRow) return;
    navigate(onClickPath || `./../${session.id}`);
  };

  const handleOnFeedback = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFeedback) {
      onFeedback(session);
    }
  };

  const isDoneOrCancelled =
    session.status === SessionStatus.DONE ||
    session.status === SessionStatus.CANCELLED;
  const happeningToday = isToday(new Date(session.startedAt));
  const joinUrl = useSessionJoinUrl(session, {
    enabled: !isDoneOrCancelled && happeningToday,
  });
  const showJoinNow =
    !!joinUrl?.startsWith('https:') && !isDoneOrCancelled && happeningToday;
  const missingFeedback = !session.feedback?.id;
  const showfeedbackCta =
    !isNativeAppointmentRow &&
    user.id === session.userId &&
    missingFeedback &&
    type === SessionListType.Past;
  const showSharedSummaryLink = shouldQuerySharedSummary && !!sharedSummary;
  const reflectionArtifact = momentsSessionArtifacts?.items.find((artifact) =>
    isMemberReflectionProfile(artifact.profileSlug),
  );
  // Both member row variants retain the shared reflection chip. Asset access
  // depends on resolved member-self eligibility, not rollout enrollment.
  const showReflectionLink = shouldQueryReflection && !!reflectionArtifact;
  // A reflection implies a transcript, so the label only speaks for the cards
  // that have no chip to show.
  const notTranscribed = useSessionNotTranscribed({
    legacySessionId: session.id,
    skip: !shouldQueryReflection,
  });
  const showNotTranscribed =
    shouldQueryReflection && notTranscribed && !showReflectionLink;
  const showMenu = !(type === SessionListType.Past && isNativeAppointmentRow);

  const handleSummaryLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
  };

  return (
    <Paper
      className={clsx('p-6', {
        '!p-0 border-none': isEmbedded,
        'cursor-pointer': !isNativeAppointmentRow,
        'h-[90px] rounded-xl border-none bg-white': presentation === 'matched',
      })}
      elevation={presentation === 'matched' ? 1 : undefined}
      // eslint-disable-next-line react/forbid-component-props -- TODO: Evaluate if we can refactor this. This component may be a Card instead of Paper. Maybe.
      sx={{
        ':hover': {
          boxShadow: !isEmbedded && !isNativeAppointmentRow ? THREE : ZERO,
        },
      }}
      variant={presentation === 'matched' ? undefined : 'outlined'}
      onClick={onSessionClick}
    >
      <div className="flex w-full flex-col ">
        <div className="flex gap-6">
          {presentation !== 'matched' && (
            <div className="hidden cursor-default md:block">
              <Avatar
                src={concerningUser.avatarUrl}
                onClick={isNativeAppointmentRow ? undefined : handleViewProfile}
              />
            </div>
          )}
          {/* `min-w-0` is required at every level between the card and the
              reflection chip: a `flex-1` box defaults to `min-width: auto`, so
              without it the chip's snippet widens the row and shoves the
              actions column past the card's right edge instead of truncating. */}
          <div className="relative flex min-w-0 flex-1 flex-col justify-between p-0 md:flex-row">
            {/* `min-w-0 flex-1` keeps the full-width reflection chip inside this
                column so it truncates instead of pushing the actions menu. */}
            <div className="flex min-w-0 flex-1 flex-col">
              <SessionCardInfo
                name={concerningUser.fullName}
                notTranscribed={showNotTranscribed}
                presentation={presentation}
                session={session}
              />
              {showSharedSummaryLink && (
                <SummaryLink
                  to={`./../${session.id}/summary`}
                  onClick={handleSummaryLinkClick}
                />
              )}
              {showReflectionLink && (
                <SessionReflectionChip
                  artifact={reflectionArtifact}
                  className="mt-2"
                  onClick={handleSummaryLinkClick}
                />
              )}
            </div>
            <div className="flex shrink-0 items-start gap-1">
              {showJoinNow && (
                <Button
                  className="w-full md:w-auto"
                  startIcon={<Icon>videocam</Icon>}
                  variant="contained"
                  onClick={() => window.open(joinUrl)}
                >
                  {presentation === 'matched'
                    ? 'Join Google Meet'
                    : 'Join session'}
                </Button>
              )}
              {showfeedbackCta && (
                <Button variant="contained" light onClick={handleOnFeedback}>
                  {`How was your ${session.isChemistryCall ? 'chemistry call' : 'session'}?`}
                </Button>
              )}
              {!isNativeAppointmentRow && (
                <Feedback
                  handleOnFeedback={handleOnFeedback}
                  session={session}
                />
              )}
              {showMenu && (
                <div className="absolute right-0 top-0 md:static">
                  <SessionCardMenu
                    enforceMemberRescheduleMinimumNotice={
                      enforceMemberRescheduleMinimumNotice &&
                      !isMomentsSession(session)
                    }
                    enforceMemberRescheduleStartBoundary={
                      isMemberRescheduleActor
                    }
                    handleOpenMenuOptions={handleOpenMenuOptions}
                    session={session}
                    type={type}
                    onCancel={onCancel}
                    onFeedback={isNativeAppointmentRow ? undefined : onFeedback}
                    onMessage={
                      isNativeAppointmentRow ? undefined : handleOnMessage
                    }
                    onReschedule={onReschedule}
                    onViewProfile={
                      isEmbedded || isNativeAppointmentRow
                        ? undefined
                        : handleViewProfile
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Paper>
  );
};

export { SessionCard };
